import { useDelete, useOne } from '@refinedev/core';
import { useTracking } from 'common/utils';
import dayjs from 'dayjs';
import { Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrganization } from 'src/pages/organization/organization.provider';

import { CropwiseAiHistoryModel } from '../cropwise-ai.model';

interface UseCropwiseAiHistoryProps {
  sessionId: string | null;
  setSessionId: Dispatch<SetStateAction<string | null>>;
  setHistoryOpen: Dispatch<SetStateAction<boolean>> | undefined;
  historyVersion: number;
}

export const useCropwiseAiHistory = ({
  sessionId,
  setSessionId,
  setHistoryOpen,
  historyVersion,
}: UseCropwiseAiHistoryProps) => {
  const { mutateAsync: mutateDelete } = useDelete();
  const { t } = useTranslation();
  const { track } = useTracking();
  const { currentOrganizationId } = useOrganization();

  const { data, refetch } = useOne<CropwiseAiHistoryModel[]>({
    resource: 'balance-ai/conversations/history',
    id: '',
    meta: {
      organizationId: currentOrganizationId,
    },
  });

  useEffect(() => {
    if (sessionId) {
      refetch();
    }
  }, [sessionId]);

  useEffect(() => {
    if (historyVersion > 0) {
      refetch();
    }
  }, [historyVersion]);

  const handleDelete = async (deletedId: string) => {
    track('Cropwise Ai History - Delete Conversation');
    if (deletedId === sessionId) {
      setSessionId(null);
    }
    mutateDelete({
      resource: 'balance-ai/conversations',
      id: deletedId,
      successNotification: false,
    }).then(() => {
      refetch();
    });
  };

  const handleNewConversation = () => {
    track('Cropwise Ai History - New Conversation');
    setSessionId(null);
    setHistoryOpen?.(false);
  };

  const handleOnClickSelectConversation = (sessionId: string) => {
    track('Cropwise Ai History - Select Conversation');
    setSessionId(sessionId);
    setHistoryOpen?.(false);
  };

  const conversationHistory = useMemo(
    () =>
      (data?.data ?? []).sort(
        (a, b) => dayjs(b.updatedAt).valueOf() - dayjs(a.updatedAt).valueOf(),
      ),
    [data],
  );

  const getGroupLabel = (dateString: string) => {
    const date = dayjs(dateString);
    const now = dayjs();

    if (date.isSame(now, 'day')) {
      return t('cropwise_ai.history.today');
    }
    return t('cropwise_ai.history.previous');
  };

  const items = useMemo(
    () =>
      conversationHistory?.map((conversation) => ({
        key: conversation.sessionId,
        label: conversation.title,
        description: ` ${t('cropwise_ai.history.last_update')} ${dayjs(
          conversation.updatedAt,
        ).format('DD/MM/YYYY HH:mm')}`,
        group: getGroupLabel(conversation.updatedAt),
      })),
    [conversationHistory, t],
  );

  return {
    items,
    conversationHistory,
    getGroupLabel,
    handleDelete,
    handleNewConversation,
    handleOnClickSelectConversation,
  };
};
